/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import moment from 'moment';
import { FaStar } from 'react-icons/fa6';
import { HiOutlineBriefcase } from 'react-icons/hi2';
import { IoLocationOutline } from 'react-icons/io5';
import { MdOutlineVerified } from 'react-icons/md';
import { VscTools } from 'react-icons/vsc';
import QRModal from '../../../components/QRModal';
import ProviderStatTile from './ProviderStatTile';
import ReportButton from '../../../../components/common/ReportButton';

interface ProviderHeroProps {
  businessDetails: any;
  averageRating?: number;
  totalReviews?: number;
  isRatingLoading?: boolean;
  /** Completed jobs from the public provider stats endpoint. Undefined when unavailable. */
  completedJobs?: number;
  /** Number of active, publicly visible services. */
  totalServices?: number;
  /** Rendered as the primary CTA only when supplied. */
  onHireNow?: () => void;
}

/** Vertical focal point used when the account has no stored banner position. */
const DEFAULT_BANNER_POSITION = 50;

/**
 * Normalises `businessBannerPosition` into a safe 0-100 vertical focal point.
 */
const normaliseBannerPosition = (raw: unknown): number => {
  let value: number;

  if (typeof raw === 'number') {
    value = raw;
  } else if (typeof raw === 'string' && raw.trim() !== '') {
    value = Number(raw.trim());
  } else {
    return DEFAULT_BANNER_POSITION;
  }

  if (!Number.isFinite(value)) return DEFAULT_BANNER_POSITION;

  return Math.min(100, Math.max(0, value));
};

/** Derives up to two initials for the avatar fallback. */
const getInitials = (name?: string) =>
  (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const ProviderHero = ({
  businessDetails,
  averageRating,
  totalReviews,
  isRatingLoading,
  completedJobs,
  totalServices,
  onHireNow,
}: ProviderHeroProps) => {
  const [bannerFailed, setBannerFailed] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);

  // businessDetails is now a Business object from /v1/business/by-slug/:businessSlug
  const businessName: string = businessDetails?.name || '';
  const bannerImage: string | undefined = businessDetails?.bannerImage;
  const bannerPositionRaw: unknown = businessDetails?.bannerPosition;
  const avatarImage: string | undefined = businessDetails?.profileImage;

  const tagLine = businessDetails?.tagLine || '';
  const isVerified = Boolean(businessDetails?.ISVerified);

  /** Banner is optional and may not exist on the account yet. */
  const showBanner = Boolean(bannerImage) && !bannerFailed;

  const bannerPosition = normaliseBannerPosition(bannerPositionRaw);

  const showAvatar = Boolean(avatarImage) && !avatarFailed;
  const initials = getInitials(businessName);

  const locationLabel = useMemo(() => {
    const city = businessDetails?.location?.city;
    const state = businessDetails?.location?.state;
    return [city, state].filter(Boolean).join(', ');
  }, [businessDetails?.location?.city, businessDetails?.location?.state]);

  const memberSince = useMemo(() => {
    const createdAt = businessDetails?.createdAt;
    if (!createdAt) return '';
    const parsed = moment(createdAt);
    return parsed.isValid() ? parsed.format('MMM YYYY') : '';
  }, [businessDetails?.createdAt]);

  const hasRating =
    typeof averageRating === 'number' && Number(totalReviews) > 0;
  const hasJobs = typeof completedJobs === 'number';
  const hasServices = typeof totalServices === 'number' && totalServices > 0;
  const hasAnyStat = hasRating || hasJobs || hasServices;

  const shareName = businessDetails?.businessSlug || businessName;

  return (
    <section className="relative">
      {/* Banner: real image when present, otherwise a brand-tinted gradient. */}
      <div className="relative h-[180px] w-full overflow-hidden rounded-b-2xl sm:h-[240px] lg:h-[340px]">
        {showBanner ? (
          <img
            src={bannerImage}
            alt={
              businessName
                ? `${businessName} cover banner`
                : 'Provider cover banner'
            }
            className="h-full w-full object-cover"
            style={{ objectPosition: `50% ${bannerPosition}%` }}
            onError={() => setBannerFailed(true)}
          />
        ) : (
          <div
            aria-hidden="true"
            className="h-full w-full bg-gradient-to-br from-brandnavy via-brandOcean to-brandcyan"
          />
        )}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-t from-brandnavy/40 to-transparent"
        />
      </div>

      {/* Overlapping profile card */}
      <div className="relative z-10 mx-auto -mt-16 w-full max-w-screen-2xl px-3 sm:-mt-20 sm:px-4">
        <div className="rounded-2xl border border-border bg-white p-4 shadow-lg sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-start sm:text-left">
              {/* Avatar: image when available, initials otherwise. */}
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-border bg-brandpalecyan/30 sm:h-28 sm:w-28">
                {showAvatar ? (
                  <img
                    src={avatarImage}
                    alt={
                      businessName
                        ? `${businessName} logo`
                        : 'Provider profile image'
                    }
                    className="h-full w-full object-cover"
                    onError={() => setAvatarFailed(true)}
                  />
                ) : (
                  <div
                    className="flex h-full w-full items-center justify-center bg-brandnavy text-2xl font-bold text-white"
                    role="img"
                    aria-label={
                      businessName
                        ? `${businessName} profile placeholder`
                        : 'Provider profile placeholder'
                    }
                  >
                    {initials || '—'}
                  </div>
                )}
              </div>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  {businessName && (
                    <h1 className="break-words text-2xl font-bold text-brandnavy sm:text-3xl">
                      {businessName}
                    </h1>
                  )}
                  {isVerified && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brandpalecyan/30 px-3 py-1 text-xs font-semibold text-brandOcean">
                      <MdOutlineVerified aria-hidden="true" size={14} />
                      Verified Provider
                    </span>
                  )}
                  {/* QRModal's `isIconOnly` is inverted: `true` renders the visible "Share Profile" label. */}
                  <QRModal name={shareName} isIconOnly />
                  {businessDetails?.businessId && (
                    <ReportButton
                      targetType="business"
                      targetId={businessDetails.businessId}
                      className="rounded-lg border border-blue-300 px-2 py-[3px] hover:border-destructive"
                    />
                  )}
                </div>

                {tagLine && (
                  <p className="mt-1.5 max-w-2xl break-words text-sm text-gray-600">
                    {tagLine}
                  </p>
                )}

                {(locationLabel || memberSince) && (
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gray-600 sm:justify-start">
                    {locationLabel && (
                      <span className="inline-flex items-center gap-1.5">
                        <IoLocationOutline
                          aria-hidden="true"
                          size={14}
                          className="text-brandOcean"
                        />
                        {locationLabel}
                      </span>
                    )}
                    {memberSince && (
                      <span className="inline-flex items-center gap-1.5">
                        <HiOutlineBriefcase
                          aria-hidden="true"
                          size={14}
                          className="text-brandOcean"
                        />
                        Member since {memberSince}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end lg:w-auto lg:justify-end">
              {onHireNow && (
                <button
                  type="button"
                  onClick={onHireNow}
                  className="cursor-pointer rounded-xl bg-brandnavy px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-brandOcean focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brandcyan focus-visible:ring-offset-2 motion-reduce:transition-none"
                >
                  Book a Service
                </button>
              )}
            </div>
          </div>

          {/* Stat tiles — each rendered only when real data backs it. */}
          {hasAnyStat && (
            <div className="mt-5 grid grid-cols-1 gap-3 border-t border-border pt-5 sm:grid-cols-2 lg:grid-cols-3">
              {hasRating && (
                <ProviderStatTile
                  icon={<FaStar aria-hidden="true" size={18} />}
                  value={
                    isRatingLoading ? '—' : Number(averageRating).toFixed(1)
                  }
                  valueSuffix={`(${totalReviews})`}
                  label="Rating"
                />
              )}
              {hasJobs && (
                <ProviderStatTile
                  icon={<VscTools aria-hidden="true" size={18} />}
                  value={completedJobs}
                  label="Jobs completed"
                />
              )}
              {hasServices && (
                <ProviderStatTile
                  icon={<HiOutlineBriefcase aria-hidden="true" size={18} />}
                  value={totalServices}
                  label="Active services"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProviderHero;
